import axios from "axios";
import { Post_GetCoordinates } from "../services/backend/restEndPoints";
import { toast } from "react-toastify";
import moment from "moment";
import { ErrorReportToast } from "../components/error-report-toast";
import { STATIC_USER_ROLES } from "./enums";
import { URLS } from "../router-x/urls";

export async function extractCoordinates(shortUrl) {
    try {
        // Step 1: Fetch the expanded URL
        const response = await axios.post(Post_GetCoordinates, { url: shortUrl });
        if (response.status === 200) {
            return { status: 200, latitude: response.data.latitude, longitude: response.data.longitude };
        }
        else {
            return { status: 400 }
        }
    } catch (error) {
        return { error: `Failed to fetch the URL: ${error.message}` };
    }
}

export function getOrdinal(n) {
    const suffixes = { one: "st", two: "nd", few: "rd", other: "th" };
    const rule = new Intl.PluralRules("en", { type: "ordinal" }).select(n);
    return n + suffixes[rule];
}

export const timeToDate = (timeString = "") => {
    if (!timeString || !timeString.includes(":"))
        return null;
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    return date;
};

export function dateToTime(isoDateString) {
    return moment(isoDateString).format('HH:mm');

}

export const Notify = (message, type = "success", actions = {}, errorData = null) => {
    const baseOptions = {
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        ...actions
    };

    // For error notifications, add the report button
    if (type === "error" && errorData) {
        const errorOptions = {
            ...baseOptions,
            autoClose: false, // Don't auto-close error notifications
            closeButton: false,
            data: errorData, // Pass error data to the close button
            className: 'w-[400px]'
        };
        return toast(ErrorReportToast, errorOptions);
    }

    // Standard notifications
    if (type === "success") {
        return toast.success(message, baseOptions);
    }
    else if (type === "error") {
        return toast.error(message, baseOptions);
    }
    else if (type === "info") {
        return toast.info(message, baseOptions);
    }
    else if (type === "warn") {
        return toast.warn(message, baseOptions);
    }
    else {
        return toast(message, baseOptions);
    }
}

// Enhanced error notification with automatic error data capture
export const NotifyError = (message, error = null, additionalInfo = '') => {
    const errorData = {
        message,
        type: 'error',
        statusCode: error?.response?.status || null,
        stackTrace: error?.response?.data || error?.message || error?.stack || 'No stack trace available',
        additionalInfo,
        severity: 'high',
        timestamp: new Date().toISOString()
    };

    return Notify(message, 'error', {}, errorData);
};

export function objectValues(obj) {
    return Object.values(obj);
}

// Function to get the keys of an object
export function objectKeys(obj) {
    return Object.keys(obj);
}

export function objectKeyValueArray(obj) {
    return Object.entries(obj).map(([key, value]) => ({ key, value }));
}

export function convertToPresentableText(inputString) {
    if (!inputString || typeof inputString !== 'string') {
        return '';
    }

    // Replace underscores with spaces
    let result = inputString.replace(/_/g, ' ');

    // Handle camelCase by adding spaces before capital letters
    result = result.replace(/([a-z])([A-Z])/g, '$1 $2');

    // Capitalize the first letter of the entire string
    result = result.charAt(0).toUpperCase() + result.slice(1);

    return result;
}

export function roundDecimal(value, n) {
    // Convert the input to a number (in case it's a string)
    const number = parseFloat(value);

    // Check if the input is a valid number
    if (isNaN(number)) {
        throw new Error('Invalid input: value must be a number or a string representation of a number');
    }

    // Use toFixed to round to n decimal places and convert back to a number
    const roundedNumber = parseFloat(number.toFixed(n));

    return roundedNumber;
}
/**
 * Converts a numeric price to words in Pakistani Rupees (PKR)
 * @param {number} price - The price to convert
 * @returns {string} The price in words
 */
export function priceToWordsPakistani(price, returnMixed = false) {
    const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
    const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "Ten", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    if (price === 0) return "Zero";

    function convertChunk(chunk, returnAsNumber = false) {
        if (returnAsNumber) {
            return chunk.toString();
        }

        let words = "";
        if (chunk >= 100) {
            words += units[Math.floor(chunk / 100)] + " Hundred ";
            chunk %= 100;
        }
        if (chunk >= 20) {
            words += tens[Math.floor(chunk / 10)] + " ";
            chunk %= 10;
        } else if (chunk >= 10) {
            words += teens[chunk - 10] + " ";
            chunk = 0;
        }
        if (chunk > 0 && chunk < 10) {
            words += units[chunk] + " ";
        }
        return words.trim();
    }

    let words = "";
    if (price >= 10000000) {
        words += convertChunk(Math.floor(price / 10000000)) + " Crore ";
        price %= 10000000;
    }
    if (price >= 100000) {
        words += convertChunk(Math.floor(price / 100000)) + " Lakh ";
        price %= 100000;
    }
    if (price >= 1000) {
        words += convertChunk(Math.floor(price / 1000)) + " Thousand ";
        price %= 1000;
    }
    if (price >= 100) {
        words += convertChunk(Math.floor(price / 100)) + " Hundred ";
        price %= 100;
    }

    // For the last chunk (less than 100), decide whether to return as number or words
    if (returnMixed) {
        words += convertChunk(price, true); // Return as number
    } else {
        words += convertChunk(price); // Return as words
    }

    return words.trim();
}

export function priceToWords(price, returnMixed = false) {
    const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
    const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "Ten", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    if (price === 0) return "Zero";

    function convertChunk(chunk, returnAsNumber = false) {
        if (returnAsNumber) {
            return chunk.toString();
        }

        let words = "";
        if (chunk >= 100) {
            words += units[Math.floor(chunk / 100)] + " Hundred, ";
            chunk %= 100;
        }
        if (chunk >= 20) {
            words += tens[Math.floor(chunk / 10)] + " ";
            chunk %= 10;
        } else if (chunk >= 10) {
            words += teens[chunk - 10] + " ";
            chunk = 0;
        }
        if (chunk > 0 && chunk < 10) {
            words += units[chunk] + " ";
        }
        return words.trim();
    }

    let words = "";
    if (price >= 1000000000) {
        words += convertChunk(Math.floor(price / 1000000000)) + " Billion ";
        price %= 1000000000;
    }
    if (price >= 1000000) {
        words += convertChunk(Math.floor(price / 1000000)) + " Million ";
        price %= 1000000;
    }
    if (price >= 1000) {
        words += convertChunk(Math.floor(price / 1000)) + " Thousand ";
        price %= 1000;
    }
    if (price >= 100) {
        words += convertChunk(Math.floor(price / 100)) + " Hundred ";
        price %= 100;
    }

    // For the last chunk (less than 100), decide whether to return as number or words
    if (returnMixed) {
        words += convertChunk(price, true); // Return as number
    } else {
        words += convertChunk(price); // Return as words
    }

    return words.trim();
}

/**
 * Formats a number with commas as thousands separators using Pakistani numbering format
 * @param {number} number - The number to format
 * @returns {string} The formatted number with commas (e.g., 1,23,45,678)
 */
export function formatNumberWithCommasPakistani(number) {
    if (isNaN(number)) {
        return 'Invalid number';
    }
    // return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");


    // Convert to string and handle decimals
    const parts = number.toString().split('.');

    // For integer part, first put a comma after 3 digits from the right
    // and then after every 2 digits from right to left (lakh, crore system)
    parts[0] = parts[0].replace(/\B(?=(\d{3})(?=(\d{3})*(?!\d)))/g, ',');

    // Special case for last group that might not have been caught
    // This ensures comma after every 2 digits for large numbers
    parts[0] = parts[0].replace(/\B(?=(\d{2})(?=(\d{3})+(?!\d)))/g, ',');

    // Join the integer and decimal parts (if any)
    return parts.join('.');
}

export function formatNumberWithCommas(number) {
    if (isNaN(number)) {
        return 'Invalid number';
    }
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function minutesToText(minutes) {
    const duration = moment.duration(minutes, 'minutes');
    const hours = Math.floor(duration.asHours());
    const mins = duration.minutes();

    const parts = [];
    if (hours > 0) {
        parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    }
    if (mins > 0) {
        parts.push(`${mins} minute${mins !== 1 ? 's' : ''}`);
    }

    return parts.join(' ') || '0 minutes';
}

export function buildParams(filters = {}) {
    return Object.entries(filters)
        .map(([key, value]) => {
            if (typeof value === "object") {
                console.log('value', value);
                // stringify object but don't encode
                return `${encodeURIComponent(key)}=${JSON.stringify(value)}`;
            }
            // encode normal values
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .join("&");
}

export const getHomePageRedirectUrl = (role) => {
    switch (role) {
        case STATIC_USER_ROLES.USER:
            return URLS.CUSTOMER.HOME;
        case STATIC_USER_ROLES.TEAM_MEMBER:
            return URLS.TEAM_MEMBER.HOME;
        case STATIC_USER_ROLES.RECEPTIONIST:
            return URLS.RECEPTIONIST.HOME;
        case STATIC_USER_ROLES.ADMIN | STATIC_USER_ROLES.MANAGER | STATIC_USER_ROLES.EMPLOYEE:
            return URLS.ADMIN.HOME;
        default:
            return URLS.ADMIN.ROOT;
    }
}